import ReactDOM from 'react-dom'
import { useMemo } from 'react'
import { Router } from 'react-router'
import { AuthenticationProvider } from '@axa-fr/react-oidc-context'
import { createBrowserHistory } from 'history'
import { SPARKY_FRAMEWORK, DEFAULT_ROOT_ELEMENT_ID, DEFAULT_TSG_OIDC_CONFIG } from '../framework/constants'
import type { VFC, ComponentType } from 'react'
import type { UserManagerSettings } from 'oidc-client'
import type { SparkySettings } from '../framework/microAppTypes'
import type { FrameworkVars } from '../framework/stateTypes'

const NullComponent: ComponentType = () => null
const history = createBrowserHistory()

const OidcAuthProvider: VFC<{ settings: SparkySettings }> = ({ settings }) => {
  const oidcConfig = useMemo(() => {
    const { oidc_client: varsConfig, oidc_client_id } = settings.variables[SPARKY_FRAMEWORK] as FrameworkVars
    if (!varsConfig) {
      return
    }
    const {
      config_url,
      authority = config_url,
      redirect_uri = `${window.origin}/authentication/callback`,
      response_type = 'code',
      scope: defaultScope = 'openid profile email',
      silent_redirect_uri = `${window.origin}${DEFAULT_TSG_OIDC_CONFIG.silent_redirect_uri}`,
      post_logout_redirect_uri,
      automaticSilentRenew = DEFAULT_TSG_OIDC_CONFIG.automaticSilentRenew,
      loadUserInfo = true,
    } = varsConfig
    const client_id = oidc_client_id || varsConfig.client_id
    const scope = defaultScope
    const config = {
      authority,
      client_id,
      redirect_uri,
      response_type,
      scope,
      silent_redirect_uri,
      post_logout_redirect_uri,
      automaticSilentRenew,
      loadUserInfo,
      accessTokenExpiringNotificationTime: DEFAULT_TSG_OIDC_CONFIG.accessTokenExpiringNotificationTime,
    }
    return config as UserManagerSettings
  }, [settings])

  if (!oidcConfig) {
    return null
  }
  return <Router history={history}>
    <AuthenticationProvider
      configuration={oidcConfig}
      notAuthenticated={NullComponent}
      notAuthorized={NullComponent}
      sessionLostComponent={NullComponent}
      authenticating={NullComponent}
      callbackComponentOverride={NullComponent}
    >Authenticating ...</AuthenticationProvider>
  </Router>
}

const render = (settings: SparkySettings) => {
  ReactDOM.render(
    <OidcAuthProvider settings={settings} />,
    document.getElementById(DEFAULT_ROOT_ELEMENT_ID)
  )
}

export default render
